'use client'
import { useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  setImageUrl,
  setLoading,
  setError,
} from '@/redux/slices/imageSessionSlice'
import { ref, getDownloadURL, uploadBytes } from 'firebase/storage'
import { firebaseStorage } from '@/firebase/init'
import { notify } from '@/utils/index'
import { ArrowUpTrayIcon } from '@heroicons/react/24/outline'
import { Tooltip } from 'react-tooltip'
import {
  createUploadSession,
  handleImageConversion,
} from '@/components/page-partials/quiz/mobile-upload/index'
import { ImageSessionState } from '@/types/redux'
import { trackEventMixpanel } from '@/utils/mixpanel'
import { usePathname } from 'next/navigation'
import authSelector from '@/redux/selectors/auth'
import navigationSelector from '@/redux/selectors/navigation'
import {
  LESSON_IMAGE_UPLOADED_FILE_UPLOAD,
  LESSON_LOCAL_UPLOAD_CLICK,
} from '@/utils/mixpanel/eventNames'
import useImageUpload from '@/hooks/useImageUpload'

interface Props extends React.HTMLAttributes<HTMLDivElement> {
  triggerBtnClassName?: string
  source?: string
  disablePasteListener?: boolean
}

export default function LocalImageUpload({
  triggerBtnClassName = 'h-10 pl-2 flex items-center justify-center gap-2 font-semibold text-sm text-v8-violet',
  source,
  disablePasteListener = false,
  ...props
}: Props) {
  const dispatch = useDispatch()
  const sessionId = useSelector(
    ({ imageSession }: { imageSession: ImageSessionState }) =>
      imageSession.sessionId,
  )
  const pathname = usePathname()
  const { user } = useSelector(authSelector)
  const { selectedLesson } = useSelector(navigationSelector)

  // Axi owns the paste listener at the composer level. Keep this hook for
  // other surfaces that depend on LocalImageUpload to install it.
  useImageUpload(!disablePasteListener)

  const inputRef = useRef<HTMLInputElement>(null)

  const handleFileUpload = async (event) => {
    if (!sessionId) {
      console.error('Local file upload failed, sessionId undefined ')

      return
    }

    let file = event.target.files[0]
    if (file) {
      const validImageTypes = [
        'image/jpeg',
        'image/png',
        'image/gif',
        'image/heic',
      ]

      if (
        !validImageTypes.includes(file.type) &&
        file.name.split('.').pop().toLowerCase() !== 'heic'
      ) {
        notify('error', 'Invalid file type. Please select an image.')

        return
      }

      if (file.size > 10 * 1024 * 1024) {
        notify('error', 'File size exceeds 10MB. Please select a smaller file.')

        return
      }

      dispatch(setError(false))
      dispatch(setLoading(true))

      file = await handleImageConversion(file)

      try {
        await createUploadSession(sessionId)
        const storageRef = ref(firebaseStorage, `uploads/${sessionId}.webp`)
        await uploadBytes(storageRef, file)
        const downloadURL = await getDownloadURL(storageRef)
        dispatch(setImageUrl(downloadURL))
        notify('success', 'Image uploaded successfully.')

        // track Mix Panel Event
        // No event on the demo lesson (home page).
        if (pathname !== '/') {
          trackEventMixpanel(LESSON_IMAGE_UPLOADED_FILE_UPLOAD, {
            role: user?.role,
            curriculumLevel: user?.curriculumLevel,
            examBoard: user?.examBoard,
            lessonId: selectedLesson?.id,
            source,
          })
        }
      } catch (error) {
        console.error('Image upload failed', error)
        notify('error', 'Image upload failed. Please try again.')
        dispatch(setError(true))
      } finally {
        dispatch(setLoading(false))
        // Reset the input value to allow re-uploading the same file
        if (inputRef.current) {
          inputRef.current.value = ''
        }
      }
    }
  }

  const handleButtonClick = () => {
    if (inputRef.current) {
      inputRef.current.click()

      // track Mix Panel Event
      // No event on the demo lesson (home page).
      if (pathname === '/') return

      trackEventMixpanel(LESSON_LOCAL_UPLOAD_CLICK, { source })
    }
  }

  return (
    <div id="local-image-upload" {...props}>
      <Tooltip id="uploadImageTooltip" className="z-[100] max-sm:hidden" />
      <button
        onClick={handleButtonClick}
        className={triggerBtnClassName}
        data-tooltip-id="uploadImageTooltip"
        data-tooltip-content="Upload your work"
        data-tooltip-place="right"
        type="button"
      >
        <ArrowUpTrayIcon className="w-[18px] h-[18px] fill-v8-violet" />
      </button>
      <input
        type="file"
        accept="image/*,.heic"
        ref={inputRef}
        style={{ display: 'none' }}
        onChange={handleFileUpload}
      />
    </div>
  )
}
